import React, { useState, useEffect, useContext } from "react";
import axios from "axios";
import {
  Card,
  Select,
  Row,
  Col,
  Spin,
  Typography,
  message,
  Space,
} from "antd";
import {
  RadialBarChart,
  RadialBar,
  Legend,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { AuthContext } from "../../context/AuthContext";
import { motion } from "framer-motion";

const { Title } = Typography;
const { Option } = Select;

export default function HrAnalytics() {
  const {authData, backUrl } = useContext(AuthContext);
  const [data, setData] = useState([]);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [organisations, setOrganisations] = useState([]);
  const [organisation, setOrganisation] = useState(1)
  const [loading, setLoading] = useState(false);



   const fetchOrganisation = async () => {
        const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
            headers: {
                Authorization: `Bearer ${authData?.access}`
            }
        })
        console.log("Org : ", res.data.results)
        setOrganisations(res.data.results)
    }

    useEffect(() => {
        fetchOrganisation()
    }, [])
 
  // 📊 Fetch HR Analytics data
  const fetchAnalyticsData = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${backUrl}/api/reports/hr-analytics/?organisation_id=${organisation}&month=${month}&year=${year}`
      );
      const apiData = res.data;

      let chartData = [
        {
          name: "Attrition Rate",
          value: apiData.attrition_rate ?? 0,
          fill: "#F44336",
        },
        {
          name: "Retention Rate",
          value: apiData.retention_rate ?? 0,
          fill: "#4CAF50",
        },
        {
          name: "Absenteeism Rate",
          value: apiData.absenteeism_rate ?? 0,
          fill: "#2196F3",
        },
      ];

      // Dummy fallback if all 0s
      const total = chartData.reduce((sum, i) => sum + i.value, 0);
      if (total === 0) {
        chartData = [
          { name: "Attrition Rate", value: 30, fill: "#F44336" },
          { name: "Retention Rate", value: 70, fill: "#4CAF50" },
          { name: "Absenteeism Rate", value: 50, fill: "#2196F3" },
        ];
      }

      setData(chartData);
    } catch (error) {
      console.error(error);
      message.error("Failed to fetch HR analytics data");
    } finally {
      setLoading(false);
    }
  };

 

  useEffect(() => {
    fetchAnalyticsData();
  }, [ month, year]);

  return (
    <Card
      style={{
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        padding: "20px",
      }}
    >
      {/* Header and Filters */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={4}>HR Analytics Overview</Title>
        </Col>
        <Col>
          <Space>
            {/* Organisation Filter */}
            <Select
              value={organisation}
              onChange={(value) => setOrganisation(value)}
              style={{ width: 280 }}
              placeholder="Select Organisation"
            >
              {organisations.map((item) => (
                        <Option key={item.id} value={item.id}>
                            {item.name}
                        </Option>
                    ))}
            </Select>

            {/* Month Filter */}
            <Select
              value={month}
              onChange={(value) => setMonth(value)}
              style={{ width: 120 }}
            >
              {[...Array(12)].map((_, i) => (
                <Option key={i + 1} value={i + 1}>
                  {new Date(0, i).toLocaleString("default", { month: "long" })}
                </Option>
              ))}
            </Select>

            {/* Year Filter */}
            <Select
              value={year}
              onChange={(value) => setYear(value)}
              style={{ width: 120 }}
            >
              {[2023, 2024, 2025, 2026].map((yr) => (
                <Option key={yr} value={yr}>
                  {yr}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>
      </Row>

      {/* Chart Section */}
      {loading ? (
        <Spin tip="Loading analytics..." />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <ResponsiveContainer width="100%" height={350}>
            <RadialBarChart
              innerRadius="10%"
              outerRadius="90%"
              data={data}
              startAngle={180}
              endAngle={0}
            >
              <RadialBar
                minAngle={15}
                clockWise
                dataKey="value"
                label={{ position: "insideStart", fill: "#fff" }}
              />
              <Legend
                iconSize={14}
                layout="horizontal"
                verticalAlign="bottom"
                align="center"
              />
              <Tooltip />
            </RadialBarChart>
          </ResponsiveContainer>
        </motion.div>
      )}
    </Card>
  );
}
